import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Leaf, Wrench, MoreVertical } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import Footer from "@/components/Footer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const Social = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="container mx-auto px-4 py-4">
          {/* Mobile/Tablet Layout */}
          <div className="lg:hidden relative flex flex-col items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="absolute top-0 right-0">
                  <MoreVertical className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                <DropdownMenuItem onClick={() => navigate("/home")}>
                  Volver al Inicio
                </DropdownMenuItem>
                <DropdownMenuItem onClick={signOut}>
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Leaf className="w-10 h-10 text-environmental" />
            <div className="text-center">
              <h1 className="text-xl font-bold text-foreground">Módulo Social</h1>
              <p className="text-sm text-muted-foreground">Gestión de Responsabilidad Social</p>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Leaf className="w-8 h-8 text-environmental" />
              <div>
                <h1 className="text-xl font-bold text-foreground">Módulo Social</h1>
                <p className="text-sm text-muted-foreground">Gestión de Responsabilidad Social</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                onClick={() => navigate("/home")}
              >
                Volver al Inicio
              </Button>
              <Button variant="outline" size="sm" onClick={signOut}>
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 lg:pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-foreground mb-4">
                Herramientas de Gestión Social
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Accede a las diferentes herramientas para la gestión de responsabilidad social y ambiental
              </p>
            </div>

            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Gestor Social Card */}
                <Card className="group relative cursor-pointer transition-all duration-300 hover:scale-105 hover:-translate-y-1 bg-card border-2 border-border hover:border-orange-500/50 hover:shadow-[0_10px_40px_-10px_rgba(249,115,22,0.6)] w-[200px] h-[220px] flex flex-col">
                  <CardHeader className="text-center space-y-2 pb-2 p-3 flex-shrink-0">
                    <div className="w-12 h-12 mx-auto rounded-full bg-accent flex items-center justify-center shadow-lg group-hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] transition-all duration-300">
                      <Leaf className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-sm font-bold text-foreground group-hover:text-orange-500 transition-colors leading-tight">
                      Gestor Social
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center p-3 pt-0 flex flex-col flex-grow justify-between">
                    <CardDescription className="text-xs leading-relaxed">
                      Sistema de seguimiento y control de obligaciones del Programa Social
                    </CardDescription>
                    <Button 
                      className="w-full bg-accent hover:bg-accent/90 text-white border-0 transition-all duration-300 hover:shadow-lg text-xs h-7 mt-3"
                      size="sm"
                      onClick={() => navigate("/gestor-social")}
                    >
                      Acceder al Gestor
                    </Button>
                  </CardContent>
                </Card>

                {/* Herramientas */}
                <Card className="group relative cursor-pointer transition-all duration-300 hover:scale-105 hover:-translate-y-1 bg-card border-2 border-border hover:border-orange-500/50 hover:shadow-[0_10px_40px_-10px_rgba(249,115,22,0.6)] w-[200px] h-[220px] flex flex-col">
                  <CardHeader className="text-center space-y-2 pb-2 p-3 flex-shrink-0">
                    <div className="w-12 h-12 mx-auto rounded-full bg-viable-blue flex items-center justify-center shadow-lg group-hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] transition-all duration-300">
                      <Wrench className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-sm font-bold text-foreground group-hover:text-orange-500 transition-colors leading-tight">
                      Herramientas
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center p-3 pt-0 flex flex-col flex-grow justify-between">
                    <CardDescription className="text-xs leading-relaxed">
                      Accede a todas las herramientas de gestión social
                    </CardDescription>
                    <Button 
                      className="w-full bg-viable-blue hover:bg-viable-blue/90 text-white border-0 transition-all duration-300 hover:shadow-lg text-xs h-7 mt-3"
                      size="sm"
                      onClick={() => navigate("/herramientas")}
                    >
                      Ver Herramientas
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default Social;